import { useCallback, useEffect, useState } from "react";
import { addDays, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, Pencil, CheckCircle2, ListPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { mondayOf, ymd, fmt, weekDays } from "@/lib/dates";
import { ROSTER_STATUS, SHIFT, initials } from "@/lib/colors";
import { PageHeader, Pill } from "@/components/common";
import { RosterReviewDialog } from "@/components/roster/RosterReviewDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { Button } from "@/components/ui/button";
import { DepartmentTabs } from "@/components/DepartmentTabs";
import { Skeleton } from "@/components/ui/skeleton";

const DayMini = ({ shift }) =>
  shift ? (
    <div className={`rounded-lg border px-1.5 py-1 text-[11px] leading-tight font-bold ${SHIFT[shift.location].cls}`}>
      {shift.start}–{shift.end}
      <div className="font-normal opacity-75">{SHIFT[shift.location].label}</div>
    </div>
  ) : <div className="text-[11px] text-slate-300 py-2 text-center">—</div>;

const ManagerRow = ({ row, days, onEdit, onApprove, onTask }) => {
  const { manager: m, roster: r } = row;
  const st = ROSTER_STATUS[r?.status || "new"];
  const byDate = Object.fromEntries((r?.shifts || []).map((s) => [s.date, s]));
  return (
    <tr className="border-t border-slate-100" data-testid={`team-roster-row-${m.id}`}>
      <td className="sticky left-0 bg-white z-10 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-[#EAF2F2] text-[#24474A] grid place-items-center text-xs font-bold shrink-0">{initials(m.name)}</span>
          <div className="leading-tight">
            <div className="font-bold text-sm text-slate-800">{m.name}</div>
            <div className="text-[11px] text-slate-500">{m.department}</div>
          </div>
        </div>
      </td>
      <td className="px-2"><Pill cls={st.cls} testid={`team-roster-status-${m.id}`}>{st.label}</Pill></td>
      {days.map((d) => <td key={ymd(d)} className="p-1.5 border-l border-slate-100 min-w-[96px]"><DayMini shift={byDate[ymd(d)]} /></td>)}
      <td className="px-3">
        <div className="flex gap-1 justify-end">
          <Button size="icon" variant="ghost" disabled={!r} onClick={() => onEdit(row)} title="Edit schedule" data-testid={`team-roster-edit-${m.id}`}><Pencil className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" disabled={!r || r.status === "approved" || r.status === "draft"} onClick={() => onApprove(r)} title="Approve" data-testid={`team-roster-approve-${m.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onTask(m.id)} title="Assign task" data-testid={`team-roster-task-${m.id}`}><ListPlus className="w-4 h-4" /></Button>
        </div>
      </td>
    </tr>
  );
};

export default function TeamRosters() {
  const [week, setWeek] = useState(ymd(mondayOf(new Date())));
  const [dept, setDept] = useState("all");
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [taskFor, setTaskFor] = useState(null);

  const load = useCallback(async () => {
    const params = { week_start: week };
    if (dept !== "all") params.department = dept;
    const { data } = await api.get("/rosters", { params });
    setRows(data);
  }, [week, dept]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (r) => {
    try {
      await api.post(`/rosters/${r.id}/approve`);
      toast.success("Schedule approved");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const days = weekDays(parseISO(week));
  return (
    <div>
      <PageHeader title="Team schedule" subtitle="Review, adjust and approve every team member's week." />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeek(ymd(addDays(parseISO(week), -7)))} data-testid="team-prev-week-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeek(ymd(addDays(parseISO(week), 7)))} data-testid="team-next-week-btn"><ChevronRight className="w-4 h-4" /></Button>
          <span className="text-lg sm:text-xl font-bold text-slate-800 ml-1" data-testid="team-week-label">Week of {fmt(week, "d MMM yyyy")}</span>
        </div>
        <DepartmentTabs value={dept} onChange={setDept} prefix="team-dept" />
      </div>
      {!rows ? <Skeleton className="h-[400px] rounded-2xl" /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="team-rosters-table">
          <table className="w-full min-w-[1080px] border-collapse">
            <thead>
              <tr className="bg-slate-50/70 text-left text-[11px] uppercase tracking-[0.15em] text-slate-500">
                <th className="sticky left-0 bg-slate-50 z-10 px-4 py-3 w-[200px]">Manager</th>
                <th className="px-2 py-3">Status</th>
                {days.map((d) => <th key={ymd(d)} className="px-2 py-3 border-l border-slate-100">{fmt(d, "EEE d")}</th>)}
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => <ManagerRow key={row.manager.id} row={row} days={days} onEdit={setEditing} onApprove={approve} onTask={setTaskFor} />)}
            </tbody>
          </table>
        </div>
      )}
      <RosterReviewDialog roster={editing?.roster} manager={editing?.manager} onClose={() => setEditing(null)} onDone={load} />
      <TaskDialog open={!!taskFor} presetAssignee={taskFor} onClose={() => setTaskFor(null)} onSaved={() => {}} />
    </div>
  );
}
